import { PublicLayout } from '@/components/PublicLayout';
import { LogoMark, SectionEyebrow } from '@/components/ui';
import { Scale, FileText } from 'lucide-react';

/**
 * Mentions légales — conformes à la LCEN (Loi pour la Confiance dans l'Économie
 * Numérique), article 6.III.
 *
 * Obligations couvertes :
 *   1. Identification de l'éditeur du service (nom, forme juridique, SIRET, RCS, TVA)
 *   2. Directeur de la publication
 *   3. Identification de l'hébergeur (nom, adresse, téléphone)
 *   4. Propriété intellectuelle
 *   5. Médiation de la consommation (L.612-1 conso) — NON APPLICABLE car service
 *      public local non commercial vis-à-vis des administrés, mais on garde la
 *      mention par prudence + service technique mairie comme premier contact.
 *   6. Protection des données (responsable de traitement collectivité — art. 37 RGPD)
 *
 * Placeholders {{EDITEUR_*}} à compléter par Henrico avant production.
 * Document versionné via env.LEGAL_MENTIONS_VERSION (affiché en bas).
 *
 * Refonte R12 (mai 2026) :
 *   - Header glass avec LogoMark Scale accent plat + eyebrow "Conformité LCEN" + titre Geist
 *   - 6 sections numérotées avec h2 Geist + paragraphes
 *   - Blocs identité éditeur/hébergeur/contact RGPD en Cards glass légères
 *   - Liens internes /politique-confidentialite et /cgu en text-tech-fg
 *   - Footer version document mono
 */
export function MentionsLegalesPage() {
  return (
    <PublicLayout maxWidth="lg" centered={false}>
      <article className="space-y-6 py-4 animate-fade-in">
        {/* Header */}
        <header className="flex items-start gap-3 pb-4 border-b border-border">
          <LogoMark icon={Scale} accent="tech" size="lg" />
          <div className="flex-1">
            <SectionEyebrow as="div">Conformité LCEN · article 6.III</SectionEyebrow>
            <h1 className="mt-1 font-display text-3xl sm:text-4xl font-semibold tracking-r-tight text-ink">
              Mentions légales
            </h1>
            <p className="mt-2 text-sm text-ink-subtle leading-relaxed">
              Conformément à l'article 6.III de la loi n°&nbsp;2004-575 du 21 juin 2004 pour la
              confiance dans l'économie numérique (LCEN).
            </p>
          </div>
        </header>

        {/* ============ 1. Éditeur ============ */}
        <LegalSection number="1" title="Éditeur du service">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le service «&nbsp;Vision Pro Collectivité&nbsp;» (ci-après «&nbsp;le Service&nbsp;») est édité par&nbsp;:
          </p>
          <LegalBlock>
            <strong className="text-ink">One For All Distribution</strong> — entrepreneur individuel<br />
            Siège social&nbsp;: 6 bis place du Bastion, 17100 Saintes<br />
            SIRET&nbsp;: <span className="font-mono text-ink">100 323 096 00014</span> ·{' '}
            TVA intracommunautaire&nbsp;: <span className="font-mono text-ink">TVA non applicable, art. 293 B du CGI</span>
            <br />
            Téléphone&nbsp;: 06 03 21 15 51 · Email&nbsp;:{' '}
            <LegalLink href="mailto:contact@oneforall-distribution.fr">contact@oneforall-distribution.fr</LegalLink>
          </LegalBlock>
          <p className="text-sm text-ink-muted leading-relaxed">
            <strong className="text-ink">Directeur de la publication&nbsp;:</strong>{' '}
            le représentant légal de One For All Distribution, en sa qualité d'éditeur du Service.
          </p>
        </LegalSection>

        {/* ============ 2. Hébergeur ============ */}
        <LegalSection number="2" title="Hébergement">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le Service est hébergé en Union Européenne&nbsp;:
          </p>
          <LegalBlock>
            <strong className="text-ink">Render Services, Inc.</strong> — région Frankfurt (DE)
            <br />
            525 Brannan Street, Suite 300, San Francisco, CA 94107, États-Unis (siège du
            sous-traitant)
            <br />
            Centres de données opérationnels en Union Européenne&nbsp;: Frankfurt (DE).
            <br />
            Site web&nbsp;:{' '}
            <LegalLink href="https://render.com" external>render.com</LegalLink>
            <br />
            Engagement contractuel&nbsp;: Data Processing Agreement (DPA) conforme RGPD,
            traitement exclusif en UE, clauses contractuelles types pour tout transfert
            résiduel.
          </LegalBlock>
        </LegalSection>

        {/* ============ 3. Protection des données ============ */}
        <LegalSection number="3" title="Protection des données personnelles">
          <p className="text-sm text-ink-muted leading-relaxed">
            La collectivité utilisatrice du Service est <strong className="text-ink">responsable
            du traitement</strong> des données personnelles saisies dans l'application.
            Conformément à l'article 37 du Règlement (UE) 2016/679 (RGPD), les autorités et
            organismes publics ont l'obligation de désigner un délégué à la protection des
            données. Pour toute question relative au traitement de vos données personnelles,
            adressez-vous en priorité au <strong className="text-ink">DPO de votre
            collectivité</strong>, dont les coordonnées figurent sur son site officiel.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            One For All Distribution intervient en qualité de <strong className="text-ink">sous-traitant</strong>
            au sens de l'article 28 du RGPD, pour le compte et sur instruction de la collectivité.
            L'éditeur n'a pas désigné de délégué à la protection des données, cette désignation
            n'étant pas obligatoire au regard de sa taille et de la nature de ses traitements
            propres. Point de contact de l'éditeur&nbsp;:
          </p>
          <LegalBlock>
            <strong className="text-ink">Contact protection des données&nbsp;:</strong> One For All Distribution<br />
            Email&nbsp;:{' '}
            <LegalLink href="mailto:contact@oneforall-distribution.fr">contact@oneforall-distribution.fr</LegalLink>
            <br />
            Adresse postale&nbsp;: 6 bis place du Bastion, 17100 Saintes
          </LegalBlock>
          <p className="text-sm text-ink-muted leading-relaxed">
            Vous trouverez le détail de vos droits et les modalités d'exercice dans notre{' '}
            <LegalLink href="/politique-confidentialite">Politique de confidentialité</LegalLink>.
          </p>
        </LegalSection>

        {/* ============ 4. Propriété intellectuelle ============ */}
        <LegalSection number="4" title="Propriété intellectuelle">
          <p className="text-sm text-ink-muted leading-relaxed">
            L'ensemble du Service (code source, interface, marques, logos, contenus éditoriaux)
            est la propriété exclusive de One For All Distribution ou de ses partenaires, et est
            protégé par les lois françaises et internationales relatives à la propriété
            intellectuelle (Code de la propriété intellectuelle, livre I).
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Toute reproduction, représentation, modification, publication, transmission ou
            dénaturation, totale ou partielle, du Service ou de son contenu, par quelque procédé
            que ce soit, sans autorisation préalable et écrite de One For All Distribution, est
            interdite et constitue une contrefaçon sanctionnée par les articles L.335-2 et
            suivants du Code de la propriété intellectuelle.
          </p>
          <p className="text-sm text-ink-muted leading-relaxed">
            Les contenus déposés par les utilisateurs (textes, photos, géolocalisations dans le
            cadre des signalements) restent la propriété de leurs auteurs respectifs. Voir{' '}
            <LegalLink href="/cgu">CGU</LegalLink>, article 4.
          </p>
        </LegalSection>

        {/* ============ 5. Contact ============ */}
        <LegalSection number="5" title="Contact">
          <p className="text-sm text-ink-muted leading-relaxed">
            Pour toute question relative au Service, signaler un dysfonctionnement ou un contenu
            inapproprié, contactez d'abord votre mairie via son canal habituel, ou écrivez-nous à{' '}
            <LegalLink href="mailto:contact@oneforall-distribution.fr">contact@oneforall-distribution.fr</LegalLink>.
          </p>
        </LegalSection>

        {/* ============ 6. Médiation ============ */}
        <LegalSection number="6" title="Médiation">
          <p className="text-sm text-ink-muted leading-relaxed">
            Le Service étant fourni à des collectivités territoriales dans le cadre d'une mission
            de service public local, il ne s'adresse pas à des consommateurs au sens du Code de la
            consommation. Le dispositif de médiation de la consommation (article L.612-1) ne
            s'applique donc pas. Les litiges relatifs à l'exécution du contrat relèvent des
            juridictions compétentes, le cas échéant administratives.
          </p>
        </LegalSection>

        <footer className="pt-4 border-t border-border text-xs text-ink-subtle">
          <FileText className="mr-1 inline h-3.5 w-3.5 align-text-bottom" aria-hidden="true" />
          Version du document&nbsp;: <strong className="text-ink">1.0</strong>{' '}
          (récupérée dynamiquement via{' '}
          <code className="rounded-r-md bg-surface/60 px-1 py-0.5 font-mono text-xs">GET /api/legal/versions</code>).
        </footer>
      </article>
    </PublicLayout>
  );
}

// ============================================================================
// Helpers de mise en forme légale (réutilisés par les 4 pages légales)
// ============================================================================

export function LegalSection({
  number, title, children,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl font-semibold tracking-r-snug text-ink">
        <span className="text-ink-muted font-normal mr-2 tabular-nums">{number}.</span>
        {title}
      </h2>
      {children}
    </section>
  );
}

export function LegalBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-r-lg border border-border bg-surface/50 backdrop-blur-glass p-4 text-sm text-ink leading-relaxed shadow-soft">
      {children}
    </div>
  );
}

export function LegalLink({
  href, external, children,
}: {
  href: string;
  external?: boolean;
  children: React.ReactNode;
}) {
  return (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="rounded-r-md px-0.5 text-tech-fg transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
    >
      {children}
    </a>
  );
}
